import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Star, Car, Clock, Shield, Award, ChevronLeft, ChevronRight, Share2, Heart, Info, Copy, Check, Zap, AlertCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Navbar } from '@/components/Navbar';
import { BookingForm } from '@/components/BookingForm';
import { ReviewCard } from '@/components/ReviewCard';
import { MapComponent } from '@/components/MapComponent';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Check auth on mount
  useEffect(() => {
    const headers = getAuthHeaders();
    axios.get(`${API}/auth/me`, { withCredentials: true, headers })
      .then(res => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/listings/${id}`),
          axios.get(`${API}/reviews/listing/${id}`)
        ]);
        setListing(listingRes.data);
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error('Error fetching listing:', error);
        toast.error('Listing not found');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  // Check favorites after user and listing are loaded
  useEffect(() => {
    if (user && id) {
      const headers = getAuthHeaders();
      axios.get(`${API}/favorites`, { withCredentials: true, headers })
        .then(res => {
          const favIds = res.data.favorites?.map(f => f.listing_id) || [];
          setIsFavorite(favIds.includes(id));
        })
        .catch(() => {});
    }
  }, [user, id]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = listing?.title || 'SpotFinder Parking';
    const shareText = `Check out this parking spot: ${shareTitle}`;

    // Try Web Share API first (mobile)
    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        toast.success('Shared successfully!');
        return;
      } catch (error) {
        if (error.name === 'AbortError') {
          return; // User cancelled, no need to show anything
        }
        // Fall through to clipboard
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareSuccess(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (error) {
      // Final fallback - manual copy
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShareSuccess(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setShareSuccess(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleFavorite = async () => {
    if (!user) {
      toast.error('Please sign in to save spots');
      navigate('/auth');
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, { withCredentials: true, headers });
        setIsFavorite(false);
        toast.success('Removed from saved spots');
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { withCredentials: true, headers });
        setIsFavorite(true);
        toast.success('Saved to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleContactHost = async () => {
    try {
      const headers = getAuthHeaders();
      // Send initial message to start conversation
      const res = await axios.post(`${API}/messages`, {
        recipient_id: listing.host_id,
        message: `Hi! I'm interested in your parking spot "${listing.title}".`
      }, { withCredentials: true, headers });
      
      toast.success('Message sent!');
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (error) {
      console.error('Contact host error:', error);
      if (error.response?.status === 401) {
        toast.error('Please sign in to message the host.');
        navigate('/auth');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start conversation. Try again.');
      }
    }
  };

  const handleBooking = async (bookingData) => {
    if (!user) {
      toast.error('Please sign in to book');
      navigate('/auth');
      return;
    }

    setBookingLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Check if recurring booking
      const endpoint = bookingData.is_recurring ? '/bookings/recurring' : '/bookings';
      const bookingRes = await axios.post(`${API}${endpoint}`, bookingData, { withCredentials: true, headers });
      
      if (bookingData.is_recurring) {
        // For recurring bookings, show success and redirect to bookings page
        const result = bookingRes.data;
        toast.success(`Created ${result.bookings.length} recurring bookings with 10% discount!`);
        navigate('/dashboard');
      } else {
        // Single booking - proceed to payment
        const booking = bookingRes.data;
        const paymentRes = await axios.post(`${API}/payments/stripe/create-session`, {
          booking_id: booking.booking_id,
          payment_method: 'stripe',
          origin_url: window.location.origin
        }, { withCredentials: true, headers });
        window.location.href = paymentRes.data.checkout_url;
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Booking failed. Please try again.');
      setBookingLoading(false);
    }
  };

  const nextPhoto = () => {
    if (listing?.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % listing.photos.length);
    }
  };

  const prevPhoto = () => {
    if (listing?.photos?.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + listing.photos.length) % listing.photos.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} />
        <div className="pt-20 flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const photos = listing.photos?.length > 0 
    ? listing.photos 
    : ['https://images.unsplash.com/photo-1571799923032-df849d54bcb9?w=800'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} />

      <div className="pt-20 md:pt-24">
        {/* Photo Gallery */}
        <div className="relative h-[40vh] md:h-[50vh] bg-slate-100">
          <img
            src={photos[currentPhotoIndex]}
            alt={listing.title}
            className="w-full h-full object-cover"
          />
          
          {/* Photo Navigation */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="prev-photo-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="next-photo-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                {photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPhotoIndex(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {/* Priority Listing badge (first 3,000 driveways) */}
            {listing.is_priority_listing && (
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold px-3 py-1.5 rounded-full shadow-lg">
                <Zap className="w-4 h-4" />
                Priority Listing
              </div>
            )}
            {/* Early Host badge (first 1,000 hosts) */}
            {listing.is_early_adopter && (
              <div className="badge-early-adopter">
                <Award className="w-4 h-4" />
                Early Host
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Button 
              variant="secondary" 
              size="icon" 
              className="rounded-full bg-white/90 hover:bg-white"
              onClick={handleShare}
              data-testid="share-btn"
            >
              {shareSuccess ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
            </Button>
            <Button 
              variant="secondary" 
              size="icon" 
              className={`rounded-full ${isFavorite ? 'bg-red-50 hover:bg-red-100' : 'bg-white/90 hover:bg-white'}`}
              onClick={handleFavorite}
              data-testid="favorite-btn"
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="container-main py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Title & Location */}
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                  {listing.title}
                </h1>
                <div className="flex items-center gap-4 text-slate-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {listing.address_visible ? (
                      <span>{listing.address}, {listing.suburb} {listing.postcode}</span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-amber-500" />
                        <span className="text-amber-700">{listing.suburb} area</span>
                        <span className="text-xs text-slate-400">(exact address revealed after payment)</span>
                      </span>
                    )}
                  </div>
                  {listing.average_rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 star-filled" />
                      <span className="font-medium">{listing.average_rating.toFixed(1)}</span>
                      <span className="text-slate-400">({listing.review_count} reviews)</span>
                    </div>
                  )}
                </div>
                
                {/* Address Protection Notice */}
                {!listing.address_visible && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                    <Lock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <strong>Address protected</strong> - The exact address and access instructions will be revealed only after your booking payment is confirmed. This protects hosts from unauthorized parking.
                    </div>
                  </div>
                )}
              </div>

              {/* Host Info */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={listing.host?.picture} />
                  <AvatarFallback className="bg-[#5ebc67] text-white text-lg">
                    {listing.host?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span>Hosted by {listing.host?.name}</span>
                    {listing.host?.is_early_adopter && (
                      <Badge className="bg-amber-100 text-amber-700">Early Host</Badge>
                    )}
                    {/* Host Reliability Badge */}
                    {listing.host_reliability && (
                      <Badge 
                        className={`${
                          listing.host_reliability.badge === 'excellent' ? 'bg-green-100 text-green-700' :
                          listing.host_reliability.badge === 'good' ? 'bg-blue-100 text-blue-700' :
                          listing.host_reliability.badge === 'fair' ? 'bg-yellow-100 text-yellow-700' :
                          listing.host_reliability.badge === 'poor' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}
                        title={`Reliability Score: ${listing.host_reliability.score}%`}
                      >
                        {listing.host_reliability.badge === 'excellent' && '⭐ '}
                        {listing.host_reliability.badge === 'good' && '✓ '}
                        {listing.host_reliability.badge.charAt(0).toUpperCase() + listing.host_reliability.badge.slice(1)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    {listing.host_reliability?.stats?.completed_bookings > 0 && (
                      <span>{listing.host_reliability.stats.completed_bookings} bookings</span>
                    )}
                    {listing.host_reliability?.stats?.average_rating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        {listing.host_reliability.stats.average_rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  {/* Warning if host has issues */}
                  {listing.host_reliability?.warnings?.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {listing.host_reliability.warnings[0]}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!user) {
                      toast.error('Please sign in to contact host');
                      navigate('/auth');
                      return;
                    }
                    handleContactHost();
                  }}
                  data-testid="contact-host-btn"
                >
                  Contact Host
                </Button>
              </div>

              <Separator />

              {/* Description */}
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">About this spot</h2>
                <p className="text-slate-600 leading-relaxed">{listing.description || 'No description provided.'}</p>
              </div>

              {/* Features */}
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">What's included</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Car className="w-5 h-5 text-[#5ebc67]" />
                    <div>
                      <p className="font-medium text-slate-900">Vehicle sizes</p>
                      <p className="text-sm text-slate-500 capitalize">{listing.vehicle_sizes?.join(', ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Clock className="w-5 h-5 text-[#5ebc67]" />
                    <div>
                      <p className="font-medium text-slate-900">Flexible hours</p>
                      <p className="text-sm text-slate-500">Book by the hour or day</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <Shield className="w-5 h-5 text-[#5ebc67]" />
                    <div>
                      <p className="font-medium text-slate-900">Secure payment</p>
                      <p className="text-sm text-slate-500">Protected by SpotFinder</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Access Instructions */}
              {listing.access_instructions && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Access Instructions
                  </h2>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                    {listing.access_instructions}
                  </div>
                </div>
              )}

              {/* Map */}
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Location</h2>
                <div className="h-64 rounded-2xl overflow-hidden">
                  <MapComponent
                    listings={[listing]}
                    center={listing.location?.coordinates || [151.2093, -33.8688]}
                    zoom={15}
                    interactive={false}
                    showUserLocation={false}
                  />
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Exact address provided after booking
                </p>
              </div>

              <Separator />

              {/* Reviews */}
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
                  Reviews
                  {listing.review_count > 0 && (
                    <span className="text-base font-normal text-slate-500">
                      ({listing.review_count})
                    </span>
                  )}
                </h2>
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map(review => (
                      <ReviewCard key={review.review_id} review={review} />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">No reviews yet. Be the first to book!</p>
                )}
              </div>
            </div>

            {/* Booking Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-white rounded-2xl border border-slate-200 shadow-lg p-6">
                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold text-slate-900">${listing.price_hourly}</span>
                    <span className="text-slate-500">/hour</span>
                  </div>
                  {listing.price_daily && (
                    <p className="text-sm text-slate-500">
                      ${listing.price_daily}/day for 8+ hours
                    </p>
                  )}
                </div>

                <Separator className="mb-6" />

                {/* Booking Form */}
                <BookingForm
                  listing={listing}
                  onSubmit={handleBooking}
                  loading={bookingLoading}
                />

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Shield className="w-4 h-4 text-[#5ebc67]" />
                    <span>Secure checkout</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    You won't be charged until your booking is confirmed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailPage;
